var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { makeAutoObservable, runInAction } from 'mobx';
import _ from 'lodash';
import moment from 'moment-timezone';
import { officesService, schedulesService } from 'ziphy-web-shared/basic/api';
import { $auth } from 'ziphy-web-shared/basic/entities/auth';
import { $loader, $windowSize, globalLoaderHandlers, localStore, } from 'ziphy-web-shared/basic/utils';
import { formatDate } from 'ziphy-web-shared/basic/utils/time';
import { DEFAULT_LAYERS, WIDTH_BY_PROVIDER_FRACTION } from './Calendar.constants';
import { DevScheduleLog, DevSlotsLog, findTimeLimits, generateDays, generateSlots, getScheduleDayTemplate, } from './Calendar.helpers';
class CalendarStore {
    constructor() {
        this.isInitialized = false;
        this.isLoading = false;
        this.loadCount = 0;
        this.viewMode = localStore.get('userSettings.scheduleViewMode', 'week');
        this.selectedDay = null;
        this._begin = null;
        this.begin = null;
        this._end = null;
        this.end = null;
        this.timezone = null;
        this.weekType = null;
        // filters
        this.filterByPractice = 0;
        this.layers = DEFAULT_LAYERS;
        // loaded data
        this.appointments = {};
        this.travels = {};
        this.practices = {};
        this.offices = {};
        this.shifts = {};
        //
        this.slots = {};
        makeAutoObservable(this);
    }
    clear() {
        this.appointments = {};
        this.travels = {};
        this.practices = {};
        this.offices = {};
        this.shifts = {};
        //
        this.slots = {};
    }
    clearFilters() {
        this.filterByPractice = 0;
        this.layers = DEFAULT_LAYERS;
    }
    clearFull() {
        this.isInitialized = false;
        this.loadCount = 0;
        this.isLoading = false;
        this.selectedDay = null;
        this._begin = null;
        this.begin = null;
        this._end = null;
        this.end = null;
        this.timezone = null;
        this.weekType = null;
        this.clear();
        this.clearFilters();
    }
    // Computed
    get isChangedRange() {
        var _a, _b;
        const hasRange = this._begin && this._end;
        return hasRange && (!((_a = this._begin) === null || _a === void 0 ? void 0 : _a.isSame(this.begin)) || !((_b = this._end) === null || _b === void 0 ? void 0 : _b.isSame(this.end)));
    }
    get isMobile() {
        return $windowSize.width < 1320;
    }
    get isDay() {
        return this.viewMode === 'day';
    }
    get isAsDay() {
        return this.isMobile || this.isDay;
    }
    get isWeek() {
        return this.viewMode === 'week';
    }
    get timeLimits() {
        return findTimeLimits(this.schedule);
    }
    get scheduleDays() {
        return generateDays(this.begin, this.end, { timezone: this.timezone });
    }
    get schedule() {
        const result = {};
        if (_.isEmpty(this.slots))
            return result;
        /*
         * INITIALIZE
         */
        this.scheduleDays.forEach((date) => {
            result[date] = result[date] || getScheduleDayTemplate();
        });
        /*
         * FILL DATA
         */
        this.scheduleDays.forEach((date) => {
            var _a;
            const scheduleDay = result[date];
            let prevCardType = null;
            let prevCardGroup = 0;
            const SAVE_CARD = (type, props) => {
                if (prevCardType === null || (prevCardType && props.type !== prevCardType))
                    prevCardGroup++;
                let cardId = `${props.type}:${prevCardGroup}`;
                if (['strict', 'multiIds'].includes(type)) {
                    cardId += `:${props.id}`;
                }
                // Save Card
                if (scheduleDay.cards[cardId]) {
                    scheduleDay.cards[cardId].height++;
                }
                else {
                    scheduleDay.cards[cardId] = {
                        id: props.id,
                        type: props.type,
                        slotIndex: props.slotIndex,
                        columnStart: props.columnStart || 1,
                        height: props.height || 1,
                        width: props.width || scheduleDay.maxCols,
                    };
                }
                // Save Card
                prevCardType = props.type;
            };
            (_a = this.slots[date]) === null || _a === void 0 ? void 0 : _a.forEach((slot) => {
                if (this.layers.appointment && slot.appointmentIds.length) {
                    /*
                     * APPOINTMENTS
                     */
                    let ids = slot.appointmentIds;
                    let prevWidth = null;
                    ids.forEach((id, index) => {
                        if (!id)
                            return;
                        const appt = this.appointments[id];
                        if (this.filterByPractice && appt.practiceId !== this.filterByPractice)
                            return;
                        SAVE_CARD('strict', {
                            id,
                            type: 'appointment',
                            slotIndex: slot.index,
                            columnStart: (prevWidth !== null && prevWidth !== void 0 ? prevWidth : index) + 1,
                            width: WIDTH_BY_PROVIDER_FRACTION[appt.providerFraction],
                        });
                        prevWidth += WIDTH_BY_PROVIDER_FRACTION[appt.providerFraction];
                    });
                }
                else if (this.layers.travel && slot.travelIds.length) {
                    /*
                     * TRAVELS
                     */
                    const ids = slot.travelIds;
                    ids.forEach((id) => {
                        if (!id)
                            return;
                        SAVE_CARD('strict', {
                            id,
                            type: 'travel',
                            slotIndex: slot.index,
                        });
                    });
                }
                else if (this.layers.practice && slot.practiceIds.length) {
                    /*
                     * PRACTICES
                     */
                    let ids = slot.practiceIds;
                    if (this.filterByPractice) {
                        ids = ids.filter((id) => id === this.filterByPractice);
                    }
                    if (ids.length) {
                        SAVE_CARD('multiIds', {
                            id: ids.join('_'),
                            type: 'practice',
                            slotIndex: slot.index,
                        });
                    }
                }
                else if (this.layers.office && slot.officeIds.length) {
                    /*
                     * OFFICES
                     */
                    let ids = slot.officeIds;
                    if (this.filterByPractice) {
                        ids = ids.filter((id) => this.offices[id].practiceId === this.filterByPractice);
                    }
                    if (ids.length) {
                        SAVE_CARD('multiIds', {
                            id: ids.join('_'),
                            type: 'office',
                            slotIndex: slot.index,
                        });
                    }
                }
                else if (slot.shiftIds.length) {
                    /*
                     * SHIFTS
                     */
                    if (this.layers.shift) {
                        const ids = slot.shiftIds;
                        ids.forEach((id) => {
                            if (!id)
                                return;
                            SAVE_CARD('type', {
                                id,
                                type: 'shift',
                                slotIndex: slot.index,
                            });
                        });
                    }
                }
                else {
                    prevCardType = null;
                }
                if (slot.shiftIds.length) {
                    if (!scheduleDay.workingTime.begin)
                        scheduleDay.workingTime.begin = slot.begin;
                    scheduleDay.workingTime.end = slot.end;
                }
            });
            result[date] = scheduleDay;
        });
        DevScheduleLog(result);
        return result;
    }
    // Mutations
    SET_FILTER(value) {
        this.filterByPractice = value;
    }
    SET_VIEW_MODE(value) {
        this.viewMode = value;
        localStore.set('userSettings.scheduleViewMode', value);
    }
    SET_RANGE(props) {
        if (props._begin)
            this._begin = props._begin;
        if (props.begin)
            this.begin = props.begin;
        if (props._end)
            this._end = props._end;
        if (props.end)
            this.end = props.end;
    }
    SET_LOAD_RANGE(props) {
        if (props._begin)
            this._begin = props._begin;
        if (props._end)
            this._end = props._end;
    }
    SET_SELECTED_DAY(value) {
        this.selectedDay = moment.isMoment(value) ? formatDate(value, { format: 'isoShort' }) : value;
    }
    // Actions
    init(timezone, weekType = 'week') {
        return __awaiter(this, void 0, void 0, function* () {
            this.timezone = timezone;
            this.weekType = weekType;
            const day = this.selectedDay
                ? moment(this.selectedDay).startOf('day')
                : moment().tz(this.timezone).startOf('day');
            let begin = this.begin;
            let end = this.end;
            if (this.isMobile || this.isDay) {
                begin = day;
                end = begin.clone().add(1, 'day');
            }
            else {
                begin = day.clone().startOf(this.weekType);
                end = begin.clone().add(1, 'week');
            }
            runInAction(() => {
                this.SET_RANGE({ _begin: begin, begin, _end: end, end });
                this.SET_SELECTED_DAY(day);
                this.isInitialized = true;
            });
            yield $calendar.loadData();
        });
    }
    loadData(setIsLoading = true) {
        return __awaiter(this, void 0, void 0, function* () {
            const loadBegin = this._begin;
            const loadEnd = this._end;
            if (loadBegin && loadEnd && !this.isLoading) {
                if (setIsLoading) {
                    this.isLoading = true;
                }
                const response = yield this.fetchSchedule(loadBegin, loadEnd);
                runInAction(() => {
                    if ('shifts' in response) {
                        const visibleBegin = this.begin;
                        const visibleEnd = this.end;
                        const slotsToKeep = {};
                        if (visibleBegin && visibleEnd) {
                            Object.keys(this.slots).forEach((date) => {
                                const dateMoment = moment(date);
                                const isInVisibleRange = dateMoment.isSameOrAfter(visibleBegin, 'day') &&
                                    dateMoment.isBefore(visibleEnd, 'day');
                                if (isInVisibleRange) {
                                    slotsToKeep[date] = this.slots[date];
                                }
                            });
                        }
                        Object.keys(response.slots).forEach((date) => {
                            slotsToKeep[date] = response.slots[date];
                        });
                        this.slots = slotsToKeep;
                        this.appointments = Object.assign(Object.assign({}, this.appointments), _.keyBy(response.appointments, 'id'));
                        this.travels = Object.assign(Object.assign({}, this.travels), _.keyBy(response.travels, 'id'));
                        this.practices = Object.assign(Object.assign({}, this.practices), response.practices);
                        this.offices = Object.assign(Object.assign({}, this.offices), response.offices);
                        this.shifts = Object.assign(Object.assign({}, this.shifts), response.shifts);
                        const usedAppointmentIds = new Set();
                        const usedTravelIds = new Set();
                        const usedShiftIds = new Set();
                        const usedOfficeIds = new Set();
                        const usedPracticeIds = new Set();
                        Object.values(this.slots).forEach((daySlots) => {
                            daySlots.forEach((slot) => {
                                slot.appointmentIds.forEach((id) => id && usedAppointmentIds.add(id));
                                slot.travelIds.forEach((id) => id && usedTravelIds.add(id));
                                slot.shiftIds.forEach((id) => id && usedShiftIds.add(id));
                                slot.officeIds.forEach((id) => id && usedOfficeIds.add(id));
                                slot.practiceIds.forEach((id) => id && usedPracticeIds.add(id));
                            });
                        });
                        this.appointments = Object.fromEntries(Object.entries(this.appointments).filter(([id]) => usedAppointmentIds.has(Number(id))));
                        this.travels = Object.fromEntries(Object.entries(this.travels).filter(([id]) => usedTravelIds.has(Number(id))));
                        this.shifts = Object.fromEntries(Object.entries(this.shifts).filter(([id]) => usedShiftIds.has(Number(id))));
                        this.offices = Object.fromEntries(Object.entries(this.offices).filter(([id]) => usedOfficeIds.has(Number(id))));
                        this.practices = Object.fromEntries(Object.entries(this.practices).filter(([id]) => usedPracticeIds.has(Number(id))));
                    }
                    this.SET_RANGE({ _begin: loadBegin, begin: loadBegin, _end: loadEnd, end: loadEnd });
                    this.loadCount++;
                    this.isLoading = false;
                });
            }
        });
    }
    fetchSchedule(begin, end) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield schedulesService.report({
                begin: `$datetime:${moment(begin).utc().format('YYYY-MM-DDTHH:mm:ss')}`,
                end: `$datetime:${moment(end).utc().format('YYYY-MM-DDTHH:mm:ss')}`,
                filter: { eq: ['id', $auth.role.id] },
            });
            if (response.error)
                return response;
            const { appointments, travels, staff } = response.preparedValue;
            const shifts = Object.values(staff)[0].shifts;
            const { offices, practices } = yield this.fetchOfficesPractices(shifts);
            const slots = generateSlots(begin, end, { shifts, travels, appointments, offices });
            DevSlotsLog(slots);
            return { shifts, appointments, travels, slots, offices, practices };
        });
    }
    fetchOfficesPractices(shifts) {
        return __awaiter(this, void 0, void 0, function* () {
            const ids = Object.values(shifts).flatMap((shiftItem) => Object.keys(shiftItem.availableTime).map((x) => Number(x)));
            if (!ids.length)
                return { offices: {}, practices: {} };
            const response = yield officesService.list({
                filter: { in: ['id', Array.from(new Set(ids))] },
                expand: { office: ['practiceId'] },
            }, { cacheTime: 15 });
            return {
                offices: _.keyBy(response.prepared.offices.items, 'id'),
                practices: _.keyBy(response.prepared.practices, 'id'),
            };
        });
    }
}
export const $calendar = new CalendarStore();
$loader.subscribe(globalLoaderHandlers.LOGOUT, () => {
    $calendar.clearFull();
});
